import { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { agentsApi } from "api/agents";
import { AvailableAgent } from "types";
import { useTranslation } from "hooks/useTranslation";
import { PipelineItem } from "views/agent/collection/new-agent/contexts/NewAgentContext";
import PureDropDown from "components/pure-dropdown";

const DragHandleIcon = () => (
  <svg
    width="1.2em"
    height="1.2em"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className="cursor-grab"
  >
    <path d="M10 4h4v4h-4zM10 10h4v4h-4zM10 16h4v4h-4zM4 10h4v4H4zM16 10h4v4h-4z" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V4C7 3.44772 7.44772 3 8 3H16C16.5523 3 17 3.44772 17 4V6ZM18 8H6V20H18V8ZM9 11V17H11V11H9ZM13 11V17H15V11H13Z"
      fill="currentColor"
    />
  </svg>
);

const AddIcon = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5C13 4.44772 12.5523 4 12 4Z"
      fill="currentColor"
    />
  </svg>
);

interface SortablePipelineItemProps {
  id: string;
  index: number;
  pipeline: PipelineItem;
  handlePipelineChange: (index: number, field: keyof PipelineItem, value: string) => void;
  handleRemovePipeline: (index: number) => void;
  agentOptions: { value: string; label: string }[];
  loading: boolean;
}

const SortablePipelineItem: React.FC<SortablePipelineItemProps> = ({
  id,
  index,
  pipeline,
  handlePipelineChange,
  handleRemovePipeline,
  agentOptions,
  loading,
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="flex items-center gap-2"
    >
      <button {...listeners} className="text-gray-500 hover:text-gray-700">
        <DragHandleIcon />
      </button>
      <div className="flex-1 flex gap-2">
        <div className="flex-1">
          <input
            id={`pipeline-input-${index}`}
            type="text"
            placeholder={t("agents.newAgent.inputPlaceholder") || "Enter input"}
            value={pipeline.input}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handlePipelineChange(index, "input", e.target.value)
            }
            disabled={loading}
            className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none disabled:!border-none disabled:!bg-gray-100 dark:!border-white/10 dark:bg-white/0 dark:text-white dark:disabled:!bg-white/5 dark:disabled:placeholder:!text-[rgba(255,255,255,0.15)]"
          />
        </div>
        <div className="flex-1">
          <PureDropDown
            options={agentOptions}
            value={pipeline.agent_id || undefined}
            onChange={(value) => handlePipelineChange(index, "agent_id", value as string)}
            placeholder={t("agents.newAgent.selectAgent") || "Select Agent"}
            showClearButton={false}
            disabled={loading}
            className="w-full"
            buttonClassName="h-12 rounded-xl border border-gray-200 bg-white/0 px-3 py-3 text-sm text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-white/0 dark:text-white dark:hover:border-white/20"
          />
        </div>
        <div className="flex-1">
          <input
            id={`pipeline-output-${index}`}
            type="text"
            placeholder={t("agents.newAgent.outputPlaceholder") || "Enter output"}
            value={pipeline.output}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handlePipelineChange(index, "output", e.target.value)
            }
            disabled={loading}
            className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none disabled:!border-none disabled:!bg-gray-100 dark:!border-white/10 dark:bg-white/0 dark:text-white dark:disabled:!bg-white/5 dark:disabled:placeholder:!text-[rgba(255,255,255,0.15)]"
          />
        </div>
      </div>
      <button
        onClick={() => handleRemovePipeline(index)}
        className="text-red-500 hover:text-red-700 disabled:opacity-50"
        disabled={loading}
      >
        <DeleteIcon />
      </button>
    </div>
  );
};

interface PipelineConfigurationProps {
  value?: PipelineItem[];
  onChange: (pipelines: PipelineItem[]) => void;
  label?: string;
  showLabel?: boolean;
  className?: string;
}

const PipelineConfiguration: React.FC<PipelineConfigurationProps> = ({
  value = [],
  onChange,
  label,
  showLabel = true,
  className = "",
}) => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<AvailableAgent[]>([]);
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        const response = await agentsApi.getAvailable();
        setAgents(response.items || []);
      } catch (err) {
        console.error("Failed to fetch agents:", err);
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  const pipelines = value || [];

  const handleAddPipeline = () => {
    onChange([
      ...pipelines,
      { input: "", agent_id: "", output: "" },
    ]);
  };

  const handleRemovePipeline = (index: number) => {
    const newPipelines = pipelines.filter((_, i) => i !== index);
    onChange(newPipelines);
  };

  const handlePipelineChange = (index: number, field: keyof PipelineItem, value: string) => {
    const updated = pipelines.map((pipeline, i) =>
      i === index ? { ...pipeline, [field]: value } : pipeline
    );
    onChange(updated);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = pipelines.findIndex((_, i) => `pipeline-${i}` === active.id);
      const newIndex = pipelines.findIndex((_, i) => `pipeline-${i}` === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newPipelines = arrayMove(pipelines, oldIndex, newIndex);
        onChange(newPipelines);
      }
    }
  };

  const agentOptions = agents.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  const displayLabel = label || t("agents.newAgent.pipeline") || "Pipeline";

  return (
    <div className={className || "h-full w-full rounded-[20px] px-3 pt-7 md:px-8"}>
      <div className="flex flex-col gap-4">
        <div>
          {showLabel && (
            <label className="mb-2 ml-3 text-sm font-bold text-navy-700 dark:text-white">
              {displayLabel}
            </label>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pipelines.map((_, i) => `pipeline-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {pipelines.map((pipeline, index) => (
                  <SortablePipelineItem
                    key={`pipeline-${index}`}
                    id={`pipeline-${index}`}
                    index={index}
                    pipeline={pipeline}
                    handlePipelineChange={handlePipelineChange}
                    handleRemovePipeline={handleRemovePipeline}
                    agentOptions={agentOptions}
                    loading={loading}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button
            onClick={handleAddPipeline}
            className="mt-4 flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
            disabled={loading}
          >
            <AddIcon />
            <span>{t("agents.newAgent.addStep") || "Add Step"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PipelineConfiguration;

